import type { CheckSendCountryItem, CheckSendLogisticsItem, CheckSendTransportGoodsItem } from './types'
import { goodsPostCalList, queryAllTransportGoodsList, queryCountryList } from '@user-center/api/common'
import { getTransfersTextByKey } from '@user-center/utils/transferText'
import { ElMessage } from 'element-plus'
import { computed, ref } from 'vue'
import { useMallShopId } from '@/composables/useMallShopId'

interface CheckSendAvailabilityParams {
  countryCode: string
  goodCode?: string | number | null
  goodType?: string | null
  weight?: string | number | null
  showError?: boolean
  matchTransportGoods?: boolean
  transportCategoryCode?: string | number | null
  transportCategoryName?: string | null
  transportCategoryPath?: string | null
}

interface CheckSendAvailabilityResult {
  isAllowed: boolean
  list: CheckSendLogisticsItem[]
  matchedBy: Array<'goodCode' | 'goodType'>
}

const DEFAULT_WEIGHT = '1'
const countryListCache = new Map<string, CheckSendCountryItem[]>()
const transportGoodsListCache = new Map<string, CheckSendTransportGoodsItem[]>()
const transportGoodsListPendingMap = new Map<string, Promise<CheckSendTransportGoodsItem[]>>()
const availabilityCache = new Map<string, CheckSendAvailabilityResult>()
const transportAvailabilityCache = new Map<string, CheckSendAvailabilityResult>()

function normalizeGoodType(raw?: string | null) {
  const text = String(raw || '').trim()
  if (!text) {
    return ''
  }

  return text.split('/')[0].trim()
}

function buildAvailabilityCacheKey(shopId: string, params: CheckSendAvailabilityParams) {
  return [
    shopId,
    String(params.countryCode || '').trim(),
    String(params.goodCode || '').trim(),
    normalizeGoodType(params.goodType),
    String(params.weight || DEFAULT_WEIGHT).trim(),
  ].join('::')
}

function buildTransportAvailabilityCacheKey(shopId: string, params: CheckSendAvailabilityParams) {
  return [
    shopId,
    String(params.countryCode || '').trim(),
    String(params.transportCategoryCode || params.goodCode || '').trim(),
    normalizeCompareText(params.transportCategoryName || params.goodType),
    normalizeCompareText(params.transportCategoryPath),
    String(params.weight || DEFAULT_WEIGHT).trim(),
  ].join('::')
}

function dedupeLogistics(list: any[]) {
  return list.filter((entry: any, index: number, self: any[]) => {
    return index === self.findIndex(currentItem => String(currentItem.id || '') === String(entry.id || ''))
  })
}

function dedupeTransportGoods(list: CheckSendTransportGoodsItem[]) {
  return list.filter((entry, index, self) => {
    return index === self.findIndex((currentItem) => {
      return String(currentItem.code || '').trim() === String(entry.code || '').trim()
        && String(currentItem.name || '').trim() === String(entry.name || '').trim()
    })
  })
}

function isAllowedLogistics(item: any) {
  return item?.isAllow === undefined || item?.isAllow === null || Number(item?.isAllow) === 1
}

function normalizeCompareText(value: any) {
  return String(value || '')
    .toLowerCase()
    .replace(/\[[^]]*]/g, '')
    .replace(/[（）()【】]/g, '')
    .replace(/[\[\]_/\\|,，。.\s>-]/g, '')
}

function splitCategoryCodes(value: any): string[] {
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

function getTransportGoodTypeName(value: any) {
  const text = String(value || '').trim()
  if (!text) {
    return ''
  }

  return text.split('/')[0].trim()
}

function getMatchedTransportGoods(
  product: { categoryId?: string | number | null, categoryName?: string | null, categoryPath?: string | null },
  transportGoodsList: CheckSendTransportGoodsItem[],
) {
  const normalizedCategoryName = normalizeCompareText(product.categoryName)
  const normalizedCategoryPath = normalizeCompareText(product.categoryPath)
  const productCategoryCodes = splitCategoryCodes(product.categoryId)

  if (!normalizedCategoryName && !normalizedCategoryPath && productCategoryCodes.length === 0) {
    return []
  }

  return transportGoodsList.filter((item) => {
    const normalizedTransportName = normalizeCompareText(item.name)
    const transportCodes = splitCategoryCodes(item.code)
    const isTextMatched = !!normalizedTransportName
      && (
        (normalizedCategoryName
          && (
            normalizedCategoryName.includes(normalizedTransportName)
            || normalizedTransportName.includes(normalizedCategoryName)
          ))
        || (normalizedCategoryPath && normalizedCategoryPath.includes(normalizedTransportName))
      )
    const isCodeMatched = productCategoryCodes.length > 0
      && transportCodes.some(code => productCategoryCodes.includes(code))

    return isTextMatched || isCodeMatched
  })
}

function normalizeTransportGoodsList(res: any) {
  if (Array.isArray(res)) {
    return res
  }

  if (Array.isArray(res?.data)) {
    return res.data
  }

  if (Array.isArray(res?.data?.dataList)) {
    return res.data.dataList
  }

  if (Array.isArray(res?.dataList)) {
    return res.dataList
  }

  if (res && typeof res === 'object') {
    const numericKeyItems = Object.keys(res)
      .filter(key => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b))
      .map(key => res[key])
      .filter(item => item && typeof item === 'object')

    if (numericKeyItems.length) {
      return numericKeyItems
    }
  }

  return []
}

export function useCheckSendAvailability() {
  const { getShopId, shopId } = useMallShopId()
  const loadingCountries = ref(false)
  const querying = ref(false)
  const countries = ref<CheckSendCountryItem[]>([])
  const transfersMap = ref<Record<string, string>>({})

  const countryOptions = computed(() => countries.value.map((item) => {
    const label = String(item.aliasName || item.country || '').trim()
    return {
      ...item,
      label: label ? getTransfersTextByKey(label) : '',
    }
  }))

  function mergeTransfersMap(nextMap?: Record<string, string>) {
    if (!nextMap || typeof nextMap !== 'object') {
      return
    }

    transfersMap.value = {
      ...transfersMap.value,
      ...nextMap,
    }
  }

  async function loadTransportGoodsList(options?: { force?: boolean, showError?: boolean }) {
    const shopId = getShopId()
    if (!shopId) {
      if (options?.showError !== false) {
        ElMessage.warning('未获取到店铺信息，请刷新页面后重试')
      }
      return []
    }

    if (!options?.force && transportGoodsListCache.has(shopId)) {
      return transportGoodsListCache.get(shopId) || []
    }

    if (!options?.force && transportGoodsListPendingMap.has(shopId)) {
      return await transportGoodsListPendingMap.get(shopId)!
    }

    const pendingPromise = queryAllTransportGoodsList({ shopId })
      .then((res: any) => {
        mergeTransfersMap(res?.transfersMap)
        const list = normalizeTransportGoodsList(res)
        const normalizedList = Array.isArray(list) ? list : []
        transportGoodsListCache.set(shopId, normalizedList)
        return normalizedList
      })
      .catch((error: any) => {
        if (options?.showError !== false) {
          ElMessage.error(error?.msg || error?.message || '运输分类加载失败')
        }
        throw error
      })
      .finally(() => {
        transportGoodsListPendingMap.delete(shopId)
      })

    transportGoodsListPendingMap.set(shopId, pendingPromise)
    return await pendingPromise
  }

  async function loadCountryList(options?: { force?: boolean, showError?: boolean }) {
    const shopId = getShopId()
    if (!shopId) {
      if (options?.showError !== false) {
        ElMessage.warning('未获取到店铺信息，请刷新页面后重试')
      }
      return []
    }

    if (!options?.force && countryListCache.has(shopId)) {
      const cached = countryListCache.get(shopId) || []
      countries.value = cached
      return cached
    }

    loadingCountries.value = true
    try {
      const res: any = await queryCountryList({ shopId })
      mergeTransfersMap(res?.transfersMap)
      const list = Array.isArray(res?.data) ? res.data : []
      countryListCache.set(shopId, list)
      countries.value = list
      return list
    }
    catch (error: any) {
      if (options?.showError !== false) {
        ElMessage.error(error?.msg || error?.message || '目的地加载失败')
      }
      return []
    }
    finally {
      loadingCountries.value = false
    }
  }

  async function queryDirectAvailability(
    shopId: string,
    params: Required<Pick<CheckSendAvailabilityParams, 'countryCode' | 'weight'>> & Pick<CheckSendAvailabilityParams, 'goodCode' | 'goodType'>,
  ): Promise<CheckSendAvailabilityResult> {
    const countryCode = String(params.countryCode || '').trim()
    const goodCode = String(params.goodCode || '').trim()
    const goodType = normalizeGoodType(params.goodType)
    const weight = String(params.weight || DEFAULT_WEIGHT).trim()

    const cacheKey = buildAvailabilityCacheKey(shopId, {
      countryCode,
      goodCode,
      goodType,
      weight,
    })
    if (availabilityCache.has(cacheKey)) {
      return availabilityCache.get(cacheKey)!
    }

    const res: any = await goodsPostCalList({
      shopId,
      cargoTypeCode: '',
      countryCode,
      warehouseCode: '',
      weight,
      goodType,
      goodCode,
    })

    mergeTransfersMap(res?.transfersMap)
    const list = dedupeLogistics(Array.isArray(res?.data) ? res.data : [])
    const result: CheckSendAvailabilityResult = {
      isAllowed: list.some(isAllowedLogistics),
      list,
      matchedBy: [
        ...(goodCode ? ['goodCode' as const] : []),
        ...(goodType ? ['goodType' as const] : []),
      ],
    }

    availabilityCache.set(cacheKey, result)
    return result
  }

  async function queryAvailability(params: CheckSendAvailabilityParams): Promise<CheckSendAvailabilityResult> {
    const shopId = getShopId()
    if (!shopId) {
      const message = '未获取到店铺信息，请刷新页面后重试'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    const countryCode = String(params.countryCode || '').trim()
    const goodCode = String(params.goodCode || '').trim()
    const goodType = normalizeGoodType(params.goodType)
    const weight = String(params.weight || DEFAULT_WEIGHT).trim()
    const transportCategoryCode = String(params.transportCategoryCode || params.goodCode || '').trim()
    const transportCategoryName = String(params.transportCategoryName || params.goodType || '').trim()
    const transportCategoryPath = String(params.transportCategoryPath || '').trim()

    if (!countryCode) {
      const message = '请选择目的地'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    if (!params.matchTransportGoods && !goodCode && !goodType) {
      const message = '请至少传入商品编码或商品分类'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    if (params.matchTransportGoods && !transportCategoryCode && !transportCategoryName && !transportCategoryPath) {
      const message = '请至少提供一个可匹配的商品分类信息'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    querying.value = true
    try {
      if (!params.matchTransportGoods) {
        return await queryDirectAvailability(shopId, {
          countryCode,
          goodCode,
          goodType,
          weight,
        })
      }

      const transportCacheKey = buildTransportAvailabilityCacheKey(shopId, {
        countryCode,
        goodCode,
        goodType,
        weight,
        transportCategoryCode,
        transportCategoryName,
        transportCategoryPath,
      })
      if (transportAvailabilityCache.has(transportCacheKey)) {
        return transportAvailabilityCache.get(transportCacheKey)!
      }

      const transportGoodsList = await loadTransportGoodsList({
        showError: params.showError,
      })

      if (!transportGoodsList.length) {
        const result = await queryDirectAvailability(shopId, {
          countryCode,
          goodCode,
          goodType,
          weight,
        })
        transportAvailabilityCache.set(transportCacheKey, result)
        return result
      }

      const matchedTransportGoods = dedupeTransportGoods(getMatchedTransportGoods({
        categoryId: transportCategoryCode,
        categoryName: transportCategoryName,
        categoryPath: transportCategoryPath,
      }, transportGoodsList))

      if (!matchedTransportGoods.length) {
        const directResult = await queryDirectAvailability(shopId, {
          countryCode,
          goodCode,
          goodType,
          weight,
        })
        const result: CheckSendAvailabilityResult = {
          ...directResult,
          matchedBy: [
            ...(transportCategoryCode ? ['goodCode' as const] : []),
            ...(transportCategoryName ? ['goodType' as const] : []),
            ...directResult.matchedBy.filter(item => !['goodCode', 'goodType'].includes(item)),
          ],
        }
        transportAvailabilityCache.set(transportCacheKey, result)
        return result
      }

      const transportCheckEntries = await Promise.all(matchedTransportGoods.map(async (item) => {
        const itemGoodCode = String(item.code || '').trim()
        const itemGoodType = getTransportGoodTypeName(item.name)
        const result = await queryDirectAvailability(shopId, {
          countryCode,
          goodCode: itemGoodCode,
          goodType: itemGoodType,
          weight,
        })
        return {
          item,
          result,
        }
      }))

      const blockedEntries = transportCheckEntries.filter(entry => !entry.result.isAllowed)
      const result: CheckSendAvailabilityResult = {
        isAllowed: blockedEntries.length === 0,
        list: dedupeLogistics(blockedEntries.flatMap(entry => entry.result.list)),
        matchedBy: [
          ...(transportCategoryCode ? ['goodCode' as const] : []),
          ...(transportCategoryName ? ['goodType' as const] : []),
        ],
      }

      transportAvailabilityCache.set(transportCacheKey, result)
      return result
    }
    catch (error: any) {
      if (params.showError !== false) {
        ElMessage.error(error?.msg || error?.message || '可寄结果查询失败')
      }
      throw error
    }
    finally {
      querying.value = false
    }
  }

  return {
    countries,
    countryOptions,
    loadingCountries,
    querying,
    shopId,
    transfersMap,
    mergeTransfersMap,
    loadCountryList,
    queryAvailability,
  }
}

export type {
  CheckSendAvailabilityParams,
  CheckSendAvailabilityResult,
}
